import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import apiService from '../services/api';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { usePageTitle } from '../hooks/usePageTitle';
import { useUnsavedChanges } from '../contexts/UnsavedChangesContext';
import ToggleSwitch from '../components/ToggleSwitch';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';

interface NotificationChannelConfig {
  app: boolean;
  email: boolean;
}

interface NotificationSettings {
  send_invitation_email: NotificationChannelConfig;
  view_request_notification: NotificationChannelConfig;
  send_approval_email_to_viewer: NotificationChannelConfig;
  file_access_violation?: NotificationChannelConfig;
  file_shared?: NotificationChannelConfig;
  file_access_revoked?: NotificationChannelConfig;
  file_access_request?: NotificationChannelConfig;
  policy_updated?: NotificationChannelConfig;
  new_device_registered?: NotificationChannelConfig;
}

const NOTIFICATION_TYPE_KEYS: (keyof NotificationSettings)[] = [
  'send_invitation_email',
  'view_request_notification',
  'send_approval_email_to_viewer',
  'file_access_violation',
  'file_shared',
  'file_access_revoked',
  'file_access_request',
  'policy_updated',
  'new_device_registered',
];

const NOTIFICATION_TYPES_CONFIG: Record<keyof NotificationSettings, { emailOnly?: boolean }> = {
  send_invitation_email: { emailOnly: true },
  view_request_notification: {},
  send_approval_email_to_viewer: {},
  file_access_violation: {},
  file_shared: {},
  file_access_revoked: {},
  file_access_request: {},
  policy_updated: {},
  new_device_registered: {},
};

const DEFAULT_NOTIFICATION_SETTINGS: NotificationSettings = {
  send_invitation_email: { app: false, email: true },
  view_request_notification: { app: true, email: true },
  send_approval_email_to_viewer: { app: true, email: true },
  file_access_violation: { app: true, email: true },
  file_shared: { app: true, email: true },
  file_access_revoked: { app: true, email: true },
  file_access_request: { app: true, email: true },
  policy_updated: { app: true, email: false },
  new_device_registered: { app: true, email: true },
};

const AdminNotifications: React.FC = () => {
  const { t } = useTranslation('adminNotifications');
  usePageTitle(t('title'));
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { setHasUnsavedChanges, setOnSave, setIsSubmitting } = useUnsavedChanges();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  
  // Track view mode to redirect when switching to user mode
  const [viewMode, setViewMode] = useState<'user' | 'admin' | 'sysadmin'>(() => {
    try {
      const saved = localStorage.getItem('view_mode');
      if (saved) {
        const mode = saved as 'user' | 'admin' | 'sysadmin';
        if (user?.role === 'sysadmin') {
          return mode;
        } else if (user?.role === 'admin') {
          return mode === 'sysadmin' ? 'admin' : mode;
        }
        return 'user';
      }
      if (user?.role === 'admin' || user?.role === 'sysadmin') {
        return 'admin';
      }
      return 'user';
    } catch {
      return 'user';
    }
  });
  
  // Listen for view mode changes and redirect if needed
  useEffect(() => {
    const handler = (event: CustomEvent) => {
      if (event.type !== 'view-mode-changed') {
        return;
      }
      const newMode = event.detail?.viewMode;
      if (newMode && newMode !== viewMode) {
        setViewMode(newMode as 'user' | 'admin' | 'sysadmin');
        
        // If on /administration/notifications and switching to user mode, redirect to /notifications
        if (location.pathname === '/administration/notifications' && newMode === 'user') {
          navigate('/notifications', { replace: true });
        }
      }
    };
    window.addEventListener('view-mode-changed', handler as EventListener);
    return () => {
      window.removeEventListener('view-mode-changed', handler as EventListener);
    };
  }, [viewMode, location.pathname, navigate]);

  const [settings, setSettings] = useState<NotificationSettings>(DEFAULT_NOTIFICATION_SETTINGS);

  // Track unsaved changes state
  const [hasUnsavedChanges, setHasUnsavedChangesState] = useState(false);
  const [hasLicenseAlertChanges, setHasLicenseAlertChanges] = useState(false);

  // License alert settings state
  interface LicenseAlertSettings {
    app: boolean;
    email: boolean;
    thresholds?: {
      license_count?: {
        enabled: boolean;
        value: number;
        unit: 'count' | 'percentage';
      };
      expiry_time?: {
        enabled: boolean;
        value: number;
        unit: 'minutes' | 'hours' | 'days' | 'weeks' | 'months';
      };
    };
    available_licenses?: number;
    total_licenses?: number;
  }

  const [licenseAlertSettings, setLicenseAlertSettings] = useState<LicenseAlertSettings>({
    app: true,
    email: true,
    thresholds: {
      license_count: {
        enabled: false,
        value: 10,
        unit: 'count',
      },
      expiry_time: {
        enabled: false,
        value: 14,
        unit: 'days',
      },
    },
  });

  const [licenseAlertLoading, setLicenseAlertLoading] = useState(false);
  const [licenseAlertSaving, setLicenseAlertSaving] = useState(false);
  const originalLicenseAlertSettingsRef = useRef<LicenseAlertSettings | null>(null);

  // Store original settings to detect changes
  const originalSettingsRef = useRef<NotificationSettings | null>(null);

  // Load notification settings
  const loadSettings = useCallback(async () => {
    if (!user?.company_id) {
      setError(t('errors.companyIdNotFound'));
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const data = await apiService.getCompanyNotificationSettings(user.company_id);
      // Merge with defaults for any missing notification types
      const mergedSettings = { ...DEFAULT_NOTIFICATION_SETTINGS, ...data };
      setSettings(mergedSettings);
      originalSettingsRef.current = JSON.parse(JSON.stringify(mergedSettings)); // Deep copy
      setHasUnsavedChangesState(false);
      setHasUnsavedChanges(false);
      setError(null);
    } catch (err: any) {
      console.error('Failed to load notification settings:', err);
      setError(err.response?.data?.error || t('errors.loadFailed'));
      toast.error(t('toast.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [user?.company_id, setHasUnsavedChanges, t]);

  // Load license alert settings
  const loadLicenseAlertSettings = useCallback(async () => {
    // Determine if we should load system-wide or company-specific settings
    const isSysadminMode = user?.role === 'sysadmin' && viewMode === 'sysadmin';
    const isCompanyAdminMode = (user?.role === 'admin') || (user?.role === 'sysadmin' && viewMode === 'admin');
    
    if (!user?.company_id && !isSysadminMode) {
      return;
    }

    try {
      setLicenseAlertLoading(true);
      let data: LicenseAlertSettings;
      
      if (isSysadminMode) {
        // For sysadmins in sysadmin mode, load system-wide settings
        data = await apiService.getSystemWideLicenseAlertSettings();
      } else if (isCompanyAdminMode && user?.company_id) {
        // For company admins or sysadmins in admin mode, load their company settings
        data = await apiService.getCompanyLicenseAlertSettings(user.company_id);
      } else {
        setLicenseAlertLoading(false);
        return;
      }

      // Use existing values as-is (don't auto-adjust, let users see their saved values)
      // Validation will prevent saving invalid values, but we show what's saved
      const availableLicenses = (data as any).available_licenses;

      // Merge with defaults - use existing values as-is
      const mergedSettings: LicenseAlertSettings = {
        app: data.app ?? true,
        email: data.email ?? true,
        thresholds: {
          license_count: {
            enabled: data.thresholds?.license_count?.enabled ?? false,
            value: data.thresholds?.license_count?.value ?? 10,
            unit: data.thresholds?.license_count?.unit ?? 'count',
          },
          expiry_time: {
            enabled: data.thresholds?.expiry_time?.enabled ?? false,
            value: data.thresholds?.expiry_time?.value ?? 14,
            unit: data.thresholds?.expiry_time?.unit ?? 'days',
          },
        },
        available_licenses: availableLicenses,
        total_licenses: (data as any).total_licenses,
      };

      setLicenseAlertSettings(mergedSettings);
      originalLicenseAlertSettingsRef.current = JSON.parse(JSON.stringify(mergedSettings));
    } catch (err: any) {
      console.error('Failed to load license alert settings:', err);
      toast.error('Failed to load license alert settings');
    } finally {
      setLicenseAlertLoading(false);
    }
  }, [user, viewMode]);

  useEffect(() => {
    loadSettings();
    loadLicenseAlertSettings();
  }, [loadSettings, loadLicenseAlertSettings, viewMode]);

  const handleSave = useCallback(async () => {
    if (!user?.company_id) {
      toast.error(t('toast.companyIdNotFound'));
      return;
    }

    try {
      setSaving(true);
      await apiService.updateCompanyNotificationSettings(user.company_id, settings);
      originalSettingsRef.current = JSON.parse(JSON.stringify(settings)); // Deep copy
      setHasUnsavedChangesState(false);
      setHasUnsavedChanges(false);
      toast.success(t('toast.saveSuccess'));
    } catch (err: any) {
      console.error('Failed to save notification settings:', err);
      toast.error(err.response?.data?.error || t('toast.saveFailed'));
    } finally {
      setSaving(false);
    }
  }, [user?.company_id, settings, setHasUnsavedChanges, setHasUnsavedChangesState, t]);

  // Save license alert settings
  const handleSaveLicenseAlert = useCallback(async () => {
    // Determine if we should save system-wide or company-specific settings
    const isSysadminMode = user?.role === 'sysadmin' && viewMode === 'sysadmin';
    const isCompanyAdminMode = (user?.role === 'admin') || (user?.role === 'sysadmin' && viewMode === 'admin');
    
    if (!user?.company_id && !isSysadminMode) {
      toast.error(t('toast.companyIdNotFound'));
      return;
    }

    try {
      setLicenseAlertSaving(true);
      
      if (isSysadminMode) {
        // Save system-wide settings
        await apiService.updateSystemWideLicenseAlertSettings(licenseAlertSettings);
      } else if (isCompanyAdminMode && user?.company_id) {
        // Save company settings
        await apiService.updateCompanyLicenseAlertSettings(user.company_id, licenseAlertSettings);
      } else {
        toast.error(t('errors.unableToDetermineContext'));
        return;
      }

      originalLicenseAlertSettingsRef.current = JSON.parse(JSON.stringify(licenseAlertSettings));
      setHasLicenseAlertChanges(false);
      // Update hasUnsavedChanges to reflect license alert changes
      setHasUnsavedChanges(false);
      toast.success(t('toast.licenseAlertSaveSuccess'));
    } catch (err: any) {
      console.error('Failed to save license alert settings:', err);
      toast.error(err.response?.data?.error || t('toast.licenseAlertSaveFailed'));
    } finally {
      setLicenseAlertSaving(false);
    }
  }, [user, viewMode, licenseAlertSettings, setHasUnsavedChanges, t]);

  // Detect changes
  useEffect(() => {
    if (originalSettingsRef.current) {
      const changed = JSON.stringify(settings) !== JSON.stringify(originalSettingsRef.current);
      setHasUnsavedChangesState(changed);
      setHasUnsavedChanges(changed);
    }
  }, [settings, setHasUnsavedChanges]);

  // Detect license alert changes
  useEffect(() => {
    if (originalLicenseAlertSettingsRef.current) {
      const changed = JSON.stringify(licenseAlertSettings) !== JSON.stringify(originalLicenseAlertSettingsRef.current);
      setHasLicenseAlertChanges(changed);
    } else {
      setHasLicenseAlertChanges(false);
    }
  }, [licenseAlertSettings]);

  // Update hasUnsavedChanges to include license alert changes for header save button
  useEffect(() => {
    const totalChanges = hasUnsavedChanges || hasLicenseAlertChanges;
    setHasUnsavedChanges(totalChanges);
  }, [hasUnsavedChanges, hasLicenseAlertChanges, setHasUnsavedChanges]);

  // Set save handler and submitting state - combine both save handlers
  useEffect(() => {
    const combinedSave = async () => {
      if (hasUnsavedChanges && settings) {
        await handleSave();
      }
      if (hasLicenseAlertChanges && licenseAlertSettings) {
        await handleSaveLicenseAlert();
      }
    };
    setOnSave(() => combinedSave);
    return () => setOnSave(null);
  }, [setOnSave, handleSave, handleSaveLicenseAlert, hasUnsavedChanges, hasLicenseAlertChanges, settings, licenseAlertSettings]);

  useEffect(() => {
    setIsSubmitting(saving || licenseAlertSaving);
  }, [saving, licenseAlertSaving, setIsSubmitting]);

  const handleToggle = (key: keyof NotificationSettings, channel: 'app' | 'email') => {
    setSettings((prev) => {
      const newSettings = { ...prev };
      const config = newSettings[key];
      if (!config) {
        // Initialize if not present
        newSettings[key] = { app: false, email: false };
      }
      
      // For email-only notifications, app should always be false
      const typeConfig = NOTIFICATION_TYPES_CONFIG[key];
      if (typeConfig?.emailOnly && channel === 'app') {
        return prev; // Don't allow toggling app for email-only notifications
      }
      
      newSettings[key] = {
        ...newSettings[key]!,
        [channel]: !newSettings[key]![channel],
      };
      return newSettings;
    });
  };

  // All expiry time units (minutes/hours for testing; days/weeks/months for production)
  const getAvailableTimeUnits = useMemo(() => {
    return ['minutes', 'hours', 'days', 'weeks', 'months'] as const;
  }, []);

  // Update license alert threshold
  const updateLicenseAlertThreshold = (
    thresholdType: 'license_count' | 'expiry_time',
    field: 'enabled' | 'value' | 'unit',
    value: boolean | number | string
  ) => {
    // Prevent enabling thresholds if both channels are disabled
    if (field === 'enabled' && value === true) {
      if (!licenseAlertSettings.app && !licenseAlertSettings.email) {
        toast.error(t('toast.enableChannelFirst'));
        return;
      }
    }

    // Validate license count value - must be less than available licenses (for count unit only)
    if (thresholdType === 'license_count' && field === 'value') {
      const numValue = value as number;
      const availableLicenses = licenseAlertSettings.available_licenses;
      
      // Only validate if we have available_licenses (company admin) and unit is 'count'
      if (availableLicenses !== undefined && licenseAlertSettings.thresholds?.license_count?.unit === 'count') {
        if (numValue < 0) {
          toast.error(t('toast.licenseCountMin'));
          return;
        }
        if (numValue >= availableLicenses) {
          toast.error(t('toast.licenseCountMax', { count: availableLicenses, max: availableLicenses - 1 }));
          return;
        }
      } else if (numValue < 0) {
        toast.error(t('toast.licenseCountMin'));
        return;
      }
    }

    setLicenseAlertSettings((prev) => {
      const newSettings = { ...prev };
      if (!newSettings.thresholds) {
        const availableLicenses = newSettings.available_licenses;
        const defaultLicenseCountValue = availableLicenses !== undefined && availableLicenses > 0
          ? Math.max(0, availableLicenses - 1)
          : 10;
        newSettings.thresholds = {
          license_count: { enabled: false, value: defaultLicenseCountValue, unit: 'count' },
          expiry_time: { enabled: false, value: 14, unit: 'days' },
        };
      }

      if (thresholdType === 'license_count') {
        if (!newSettings.thresholds.license_count) {
          const availableLicenses = newSettings.available_licenses;
          const defaultLicenseCountValue = availableLicenses !== undefined && availableLicenses > 0
            ? Math.max(0, availableLicenses - 1)
            : 10;
          newSettings.thresholds.license_count = { enabled: false, value: defaultLicenseCountValue, unit: 'count' };
        }
        if (field === 'enabled') {
          newSettings.thresholds.license_count.enabled = value as boolean;
        } else if (field === 'value') {
          newSettings.thresholds.license_count.value = Math.floor(value as number); // Ensure integer
        } else if (field === 'unit') {
          newSettings.thresholds.license_count.unit = value as 'count' | 'percentage';
          // When switching to count unit, re-validate the value
          if (value === 'count' && newSettings.thresholds.license_count.enabled) {
            const currentValue = newSettings.thresholds.license_count.value;
            const availableLicenses = newSettings.available_licenses;
            if (availableLicenses !== undefined && currentValue >= availableLicenses) {
              // Auto-adjust to max allowed value
              newSettings.thresholds.license_count.value = Math.max(0, availableLicenses - 1);
              toast(t('toast.licenseCountAdjusted', { value: availableLicenses - 1 }), { icon: 'ℹ️' });
            }
          }
        }
      } else if (thresholdType === 'expiry_time') {
        if (!newSettings.thresholds.expiry_time) {
          newSettings.thresholds.expiry_time = { enabled: false, value: 14, unit: 'days' };
        }
        if (field === 'enabled') {
          newSettings.thresholds.expiry_time.enabled = value as boolean;
        } else if (field === 'value') {
          newSettings.thresholds.expiry_time.value = Math.floor(value as number); // Ensure integer
        } else if (field === 'unit') {
          newSettings.thresholds.expiry_time.unit = value as 'minutes' | 'hours' | 'days' | 'weeks' | 'months';
        }
      }

      return newSettings;
    });
  };

  // Handle channel toggle - disable thresholds if both channels become disabled
  const handleLicenseAlertChannelToggle = (channel: 'app' | 'email') => {
    setLicenseAlertSettings((prev) => {
      const newSettings = { ...prev };
      newSettings[channel] = !newSettings[channel];
      
      // If both channels are now disabled, disable all thresholds
      if (!newSettings.app && !newSettings.email) {
        if (newSettings.thresholds) {
          if (newSettings.thresholds.license_count) {
            newSettings.thresholds.license_count.enabled = false;
          }
          if (newSettings.thresholds.expiry_time) {
            newSettings.thresholds.expiry_time.enabled = false;
          }
        }
      }
      
      return newSettings;
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-governate-500"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 sm:p-6">
    

        {/* Only show regular notification types when in admin mode (company admins or sysadmins in admin mode) */}
        {viewMode === 'admin' && (
          <div className="space-y-6">
            {/* Desktop Table View */}
            <div className="hidden md:block overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-700">
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300">
                      {t('table.notificationType')}
                    </th>
                    <th className="text-center py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300 w-24">
                      {t('table.inApp')}
                    </th>
                    <th className="text-center py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300 w-24">
                      {t('table.email')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {NOTIFICATION_TYPE_KEYS.map((key, index) => {
                    const config = settings[key] || { app: false, email: false };
                    const typeConfig = NOTIFICATION_TYPES_CONFIG[key];
                    return (
                      <tr
                        key={key}
                        className={`${
                          index === NOTIFICATION_TYPE_KEYS.length - 1 ? '' : 'border-b border-gray-200 dark:border-gray-700'
                        } hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors`}
                      >
                        <td className="py-4 px-4">
                          <div className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            {t(`notificationTypes.${key}.label`)}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            {t(`notificationTypes.${key}.description`)}
                          </div>
                        </td>
                        <td className="py-4 px-4 text-center">
                          {typeConfig?.emailOnly ? (
                            <span className="text-xs text-gray-400 dark:text-gray-500">{t('table.na')}</span>
                          ) : (
                            <div className="flex items-center justify-center">
                              <ToggleSwitch
                                checked={config.app}
                                onChange={() => handleToggle(key, 'app')}
                              />
                            </div>
                          )}
                        </td>
                        <td className="py-4 px-4 text-center">
                          <div className="flex items-center justify-center">
                            <ToggleSwitch
                              checked={config.email}
                              onChange={() => handleToggle(key, 'email')}
                            />
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Mobile Card View */}
            <div className="md:hidden space-y-4">
              {NOTIFICATION_TYPE_KEYS.map((key) => {
                const config = settings[key] || { app: false, email: false };
                const typeConfig = NOTIFICATION_TYPES_CONFIG[key];
                return (
                  <div
                    key={key}
                    className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-white dark:bg-gray-800"
                  >
                    <div className="mb-3">
                      <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        {t(`notificationTypes.${key}.label`)}
                      </h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        {t(`notificationTypes.${key}.description`)}
                      </p>
                    </div>
                    <div className="space-y-3">
                      {!typeConfig?.emailOnly && (
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600 dark:text-gray-400">{t('table.inApp')}</span>
                          <ToggleSwitch
                            checked={config.app}
                            onChange={() => handleToggle(key, 'app')}
                          />
                        </div>
                      )}
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600 dark:text-gray-400">{t('table.email')}</span>
                        <ToggleSwitch
                          checked={config.email}
                          onChange={() => handleToggle(key, 'email')}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* License Alert Settings Section */}
        {(user?.role === 'admin' || user?.role === 'sysadmin') && (
          <div className={viewMode === 'admin' ? 'mt-8 pt-8 border-t border-gray-200 dark:border-gray-700' : ''}>
            <div className="flex items-center space-x-2 mb-6">
              <ExclamationTriangleIcon className="w-6 h-6 text-yellow-600 dark:text-yellow-500" />
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                {t('licenseAlert.title')}
              </h2>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
              {user?.role === 'sysadmin' && viewMode === 'sysadmin'
                ? t('licenseAlert.descriptionSysadmin')
                : t('licenseAlert.descriptionAdmin')}
            </p>

            {licenseAlertLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-governate-500"></div>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Notification Channels - Inline Layout */}
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                  <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-4">
                    {t('notificationChannels')}
                  </h3>
                  <div className="flex items-center gap-6">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-700 dark:text-gray-300">{t('table.inApp')}</span>
                      <ToggleSwitch
                        checked={licenseAlertSettings.app}
                        onChange={() => handleLicenseAlertChannelToggle('app')}
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-700 dark:text-gray-300">{t('table.email')}</span>
                      <ToggleSwitch
                        checked={licenseAlertSettings.email}
                        onChange={() => handleLicenseAlertChannelToggle('email')}
                      />
                    </div>
                  </div>
                </div>

                {/* License Count Threshold */}
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-sm font-medium text-gray-900 dark:text-white">
                      {t('licenseCountThreshold.title')}
                    </h3>
                    <ToggleSwitch
                      checked={licenseAlertSettings.thresholds?.license_count?.enabled ?? false}
                      onChange={(checked) => updateLicenseAlertThreshold('license_count', 'enabled', checked)}
                    />
                  </div>
                  {licenseAlertSettings.thresholds?.license_count?.enabled && (
                    <div className="space-y-3 mt-4">
                      <div className="flex items-center space-x-3">
                        <label className="text-sm text-gray-700 dark:text-gray-300">{t('licenseCountThreshold.alertWhenRemaining')}</label>
                        <input
                          type="number"
                          min="0"
                          step="1"
                          max={licenseAlertSettings.available_licenses !== undefined && licenseAlertSettings.thresholds?.license_count?.unit === 'count' 
                            ? Math.max(0, licenseAlertSettings.available_licenses - 1)
                            : undefined}
                          value={licenseAlertSettings.thresholds.license_count?.value ?? (
                            licenseAlertSettings.available_licenses !== undefined && licenseAlertSettings.available_licenses > 0
                              ? Math.max(0, licenseAlertSettings.available_licenses - 1)
                              : 10
                          )}
                          onChange={(e) => updateLicenseAlertThreshold('license_count', 'value', parseInt(e.target.value, 10) || 0)}
                          className="w-24 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                        />
                        <select
                          value={licenseAlertSettings.thresholds.license_count?.unit ?? 'count'}
                          onChange={(e) => updateLicenseAlertThreshold('license_count', 'unit', e.target.value)}
                          className="px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                        >
                          <option value="count">{t('licenseCountThreshold.count')}</option>
                          <option value="percentage">{t('licenseCountThreshold.percentage')}</option>
                        </select>
                        {licenseAlertSettings.available_licenses !== undefined && licenseAlertSettings.thresholds?.license_count?.unit === 'count' && (
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            {t('licenseCountThreshold.maxLabel', { value: Math.max(0, licenseAlertSettings.available_licenses - 1) })}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {licenseAlertSettings.thresholds.license_count?.unit === 'percentage'
                          ? t('licenseCountThreshold.hintPercentage')
                          : t('licenseCountThreshold.hintCount')}
                      </p>
                    </div>
                  )}
                </div>

                {/* Expiry Time Threshold */}
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-sm font-medium text-gray-900 dark:text-white">
                      {t('expiryTimeThreshold.title')}
                    </h3>
                    <ToggleSwitch
                      checked={licenseAlertSettings.thresholds?.expiry_time?.enabled ?? false}
                      onChange={(checked) => updateLicenseAlertThreshold('expiry_time', 'enabled', checked)}
                    />
                  </div>
                  {licenseAlertSettings.thresholds?.expiry_time?.enabled && (
                    <div className="space-y-3 mt-4">
                      <div className="flex items-center space-x-3">
                        <label className="text-sm text-gray-700 dark:text-gray-300">{t('expiryTimeThreshold.alertWhenWithin')}</label>
                        <input
                          type="number"
                          min="0"
                          step="1"
                          value={licenseAlertSettings.thresholds.expiry_time?.value ?? 14}
                          onChange={(e) => updateLicenseAlertThreshold('expiry_time', 'value', parseInt(e.target.value, 10) || 0)}
                          className="w-24 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                        />
                        <select
                          value={licenseAlertSettings.thresholds.expiry_time?.unit ?? 'days'}
                          onChange={(e) => updateLicenseAlertThreshold('expiry_time', 'unit', e.target.value)}
                          className="px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                        >
                          {getAvailableTimeUnits.map(unit => (
                            <option key={unit} value={unit}>
                              {t(`timeUnits.${unit}`)}
                            </option>
                          ))}
                        </select>
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {t('expiryTimeThreshold.hint')}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {error && (
          <div className="mt-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
            <p className="text-red-800 dark:text-red-200">{error}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminNotifications;
